// Enemy actor + AI. Deliberately readable and fair (playability): enemies only
// wake/shoot with clear line-of-sight, telegraph attacks with a short windup,
// stagger on pain, and leave a corpse on death. Greedy chase with wall-sliding
// is enough for the arena/corridor layouts and stays cheap.

import { clamp } from "./math";
import { isSolid } from "./types";
import type { Level } from "./types";
import type { RenderSprite } from "./raycaster";
import type { ActorSprites } from "./sprites";

export type EnemyRenderSprite = RenderSprite;

type State = "idle" | "chase" | "windup" | "pain" | "dead";

export interface EnemyEnv {
  readonly level: Level;
  readonly player: { x: number; y: number };
  hurtPlayer(amount: number): void;
  onDeath(scoreValue: number): void;
  playSound(name: string): void;
  rng(): number;
}

const RADIUS = 0.3;
const SPEED = 1.7;
const SIGHT = 13;
const ATTACK_RANGE = 7.5;
const WINDUP = 0.36;
const COOLDOWN = 1.25;
const PAIN_TIME = 0.28;
const WORLD_SCALE = 0.82;

/** Grid line-of-sight: true if nothing solid blocks a→b. */
export function losClear(level: Level, ax: number, ay: number, bx: number, by: number): boolean {
  const dx = bx - ax;
  const dy = by - ay;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-4) return true;
  const stepX = dx / dist;
  const stepY = dy / dist;
  const steps = Math.ceil(dist / 0.08);
  let x = ax;
  let y = ay;
  for (let i = 0; i < steps; i++) {
    x += stepX * 0.08;
    y += stepY * 0.08;
    if (isSolid(level, Math.floor(x), Math.floor(y))) return false;
  }
  return true;
}

function blocked(level: Level, x: number, y: number, r: number): boolean {
  const minX = Math.floor(x - r);
  const maxX = Math.floor(x + r);
  const minY = Math.floor(y - r);
  const maxY = Math.floor(y + r);
  for (let cy = minY; cy <= maxY; cy++) {
    for (let cx = minX; cx <= maxX; cx++) {
      if (!isSolid(level, cx, cy)) continue;
      const nx = clamp(x, cx, cx + 1);
      const ny = clamp(y, cy, cy + 1);
      if ((x - nx) ** 2 + (y - ny) ** 2 < r * r) return true;
    }
  }
  return false;
}

export class Enemy {
  x: number;
  y: number;
  readonly kind: string;
  health: number;
  readonly maxHealth: number;
  state: State = "idle";
  radius = RADIUS;
  private anim = 0;
  private timer = 0; // windup / pain timer
  private cooldown = 0;
  private deathT = 0;
  private painFlash = 0;

  // Sprites are supplied at render time (not construction) so the class stays
  // DOM-free and instantiable under the headless test runner.
  constructor(
    spawn: { x: number; y: number },
    kind: string,
    health: number,
    private readonly scoreValue = 100,
  ) {
    this.x = spawn.x;
    this.y = spawn.y;
    this.kind = kind;
    this.health = health;
    this.maxHealth = health;
  }

  get alive(): boolean {
    return this.state !== "dead";
  }

  update(dt: number, env: EnemyEnv): void {
    this.anim += dt;
    if (this.painFlash > 0) this.painFlash -= dt;
    if (this.cooldown > 0) this.cooldown -= dt;

    if (this.state === "dead") {
      this.deathT += dt;
      return;
    }

    const px = env.player.x;
    const py = env.player.y;
    const dx = px - this.x;
    const dy = py - this.y;
    const dist = Math.hypot(dx, dy);
    const canSee = dist < SIGHT && losClear(env.level, this.x, this.y, px, py);

    if (this.state === "pain") {
      this.timer -= dt;
      if (this.timer <= 0) this.state = canSee ? "chase" : "idle";
      return;
    }

    if (this.state === "windup") {
      this.timer -= dt;
      if (this.timer <= 0) {
        // Fire if the player is still visible; accuracy falls off with distance.
        if (canSee && dist < ATTACK_RANGE + 2) {
          const acc = clamp(1 - dist / (ATTACK_RANGE + 4), 0.25, 0.95);
          if (env.rng() < acc) env.hurtPlayer(6 + env.rng() * 9);
        }
        this.cooldown = COOLDOWN;
        this.state = "chase";
      }
      return;
    }

    if (!canSee) {
      this.state = "idle";
      return;
    }

    // Visible: chase, and attack when in range + off cooldown.
    if (dist <= ATTACK_RANGE && this.cooldown <= 0) {
      this.state = "windup";
      this.timer = WINDUP;
      env.playSound("enemyShoot");
      return;
    }

    this.state = "chase";
    const nx = this.x + (dx / dist) * SPEED * dt;
    const ny = this.y + (dy / dist) * SPEED * dt;
    if (!blocked(env.level, nx, this.y, this.radius)) this.x = nx;
    if (!blocked(env.level, this.x, ny, this.radius)) this.y = ny;
  }

  hit(damage: number, env: EnemyEnv): void {
    if (this.state === "dead") return;
    this.health -= damage;
    this.painFlash = 0.12;
    if (this.health <= 0) {
      this.state = "dead";
      this.deathT = 0;
      env.playSound("enemyDie");
      env.onDeath(this.scoreValue);
      return;
    }
    // Pain chance interrupts the enemy briefly (fair feedback).
    if (env.rng() < 0.55) {
      this.state = "pain";
      this.timer = PAIN_TIME;
      env.playSound("enemyPain");
    }
  }

  getRenderSprite(sprites: ActorSprites): EnemyRenderSprite {
    let frame;
    let worldScale = WORLD_SCALE;
    if (this.state === "dead") {
      const idx = Math.min(sprites.die.length - 1, Math.floor(this.deathT * 12));
      frame = sprites.die[idx]!;
      worldScale = WORLD_SCALE * (idx >= sprites.die.length - 1 ? 0.7 : 1);
    } else if (this.state === "pain") {
      frame = sprites.pain;
    } else {
      const moving = this.state === "chase";
      const i = moving ? Math.floor(this.anim * 6) % sprites.walk.length : 0;
      frame = sprites.walk[i]!;
    }
    return {
      x: this.x,
      y: this.y,
      frame,
      worldScale,
      bright: this.painFlash > 0 ? 1.9 : 1,
    };
  }
}

/** Pick the nearest alive enemy the aim ray hits (ray/circle), not blocked by a wall. */
export function pickTarget(
  enemies: readonly Enemy[],
  ox: number,
  oy: number,
  dirX: number,
  dirY: number,
  level: Level,
  maxDist = 20,
): Enemy | null {
  let best: Enemy | null = null;
  let bestT = maxDist;
  for (const e of enemies) {
    if (!e.alive) continue;
    const cx = e.x - ox;
    const cy = e.y - oy;
    const t = cx * dirX + cy * dirY; // projection onto ray
    if (t < 0 || t > bestT) continue;
    const perpX = cx - dirX * t;
    const perpY = cy - dirY * t;
    if (perpX * perpX + perpY * perpY > e.radius * e.radius) continue; // ray misses circle
    if (!losClear(level, ox, oy, e.x, e.y)) continue;
    best = e;
    bestT = t;
  }
  return best;
}
